import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn, formatCurrency } from "@/lib/utils";
import type { Position } from "@/solana/programs/types";
import { toast } from "sonner";
import { Minus, Plus, X, ArrowLeftRight, Check, Loader2 } from "lucide-react";
import { usePrice } from "@/backend/store";

type ButtonState = 'idle' | 'processing' | 'success';

type ClosePositionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: Position | null;
  onConfirm: (position: Position, closePercentage: number) => Promise<void>;
};

type ValueMode = "size" | "value"; // Size = BTC, Value = USD

const PERCENTAGE_OPTIONS = [10, 25, 50, 75, 100] as const;

export const ClosePositionModal = ({
  open,
  onOpenChange,
  position,
  onConfirm,
}: ClosePositionModalProps) => {
  const [closeAmount, setCloseAmount] = useState<string>("");
  const [valueMode, setValueMode] = useState<ValueMode>("size"); // BTC or USD
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(null); // Track clicked percentage
  const [buttonState, setButtonState] = useState<ButtonState>('idle'); // For success animation
  
  // Get live price from store (safe even if position is null)
  const livePrice = usePrice(position?.market || "");
  
  // Reset when modal opens - default to 100% of position size
  // Note: Only reset when modal opens, not when price changes (livePrice not in deps)
  useEffect(() => {
    if (open && position) {
      const currentPrice = livePrice > 0 ? livePrice : position.currentPrice;
      // Default to full size in the current mode
      if (valueMode === "size") {
        setCloseAmount(position.size.toFixed(4));
      } else {
        setCloseAmount((position.size * currentPrice).toFixed(2));
      }
      setSelectedPercentage(100); // Default to 100%
      setButtonState('idle'); // Reset button state
    }
  }, [open, position, valueMode]); // Removed livePrice to prevent constant resets
  
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const closeAmountNum = Number(closeAmount) || 0;
  
  // Handle percentage button clicks - calculate amount based on percentage
  const handlePercentageClick = useCallback((percentage: number) => {
    if (!position) return;
    const currentPrice = livePrice > 0 ? livePrice : position.currentPrice;
    
    // For exact percentages, calculate from position size directly
    const targetSize = percentage === 100 
      ? position.size  // Exact full size for 100%
      : position.size * (percentage / 100);
    const targetValue = targetSize * currentPrice;
    
    // Store which percentage was selected for display
    setSelectedPercentage(percentage);
    
    if (valueMode === "size") {
      setCloseAmount(targetSize.toFixed(4));
    } else {
      setCloseAmount(targetValue.toFixed(2));
    }
  }, [position, livePrice, valueMode]);
  
  // Handle +/- buttons
  const handleIncrement = useCallback(() => {
    if (!position) return;
    const current = closeAmountNum || 0;
    const currentPrice = livePrice > 0 ? livePrice : position.currentPrice;
    const step = valueMode === "size" ? 0.0001 : 1;
    const max = valueMode === "size" ? position.size : position.size * currentPrice;
    const newAmount = Math.min(current + step, max);
    setCloseAmount(newAmount.toFixed(valueMode === "size" ? 4 : 2));
    setSelectedPercentage(null); // Manual adjustment, clear selected percentage
  }, [position, closeAmountNum, livePrice, valueMode]);
  
  const handleDecrement = useCallback(() => {
    const current = closeAmountNum || 0;
    const step = valueMode === "size" ? 0.0001 : 1;
    const newAmount = Math.max(current - step, 0);
    setCloseAmount(newAmount > 0 ? newAmount.toFixed(valueMode === "size" ? 4 : 2) : "");
    setSelectedPercentage(null); // Manual adjustment, clear selected percentage
  }, [closeAmountNum, valueMode]);
  
  const handleSubmit = useCallback(async () => {
    if (!position || buttonState !== 'idle') return;
    
    const currentPrice = livePrice > 0 ? livePrice : position.currentPrice;
    
    // Convert amount to size - CLAMP to position size (same as UI calculation)
    let closeSize: number;
    if (valueMode === "size") {
      closeSize = Math.min(closeAmountNum, position.size); // Clamp to never exceed position
    } else {
      const calculatedSize = closeAmountNum / currentPrice;
      closeSize = Math.min(calculatedSize, position.size); // Clamp to never exceed position
    }
    
    // Use selected percentage if user clicked a percentage button
    // Otherwise calculate from the close size
    // This ensures we close exactly the intended percentage of current position
    const closePercentage = selectedPercentage ?? (closeSize / position.size) * 100;
    
    // Basic validation
    if (closeSize <= 0 || closeAmount === "") {
      return;
    }
    
    // ✨ OPTIMIZED UX: Fire API immediately in background, animate independently
    // Modal closes on timer - doesn't wait for API
    setButtonState('processing');
    
    // Fire API immediately in background - DON'T await it
    // Toast will appear when API completes (success or error)
    onConfirm(position, closePercentage).catch((error) => {
      const message = error instanceof Error ? error.message : "Failed to close position";
      toast.error(message);
    });
    // Success toast is shown by handleConfirmClose in positions-panel when API completes
    
    // Show spinner for 250ms (API already firing in background)
    await new Promise(resolve => setTimeout(resolve, 250));
    
    // Show checkmark for 250ms
    setButtonState('success');
    await new Promise(resolve => setTimeout(resolve, 250));
    
    // Close modal - API may or may not be done, doesn't matter
    onOpenChange(false);
  }, [position, buttonState, closeAmountNum, valueMode, selectedPercentage, closeAmount, livePrice, onConfirm, onOpenChange]);
  
  // Toggle between size and value mode
  const handleToggleMode = useCallback(() => {
    if (!position) return;
    const currentPrice = livePrice > 0 ? livePrice : position.currentPrice;
    
    if (valueMode === "size") {
      // Switching to USD - convert current size amount to USD
      const sizeValue = closeAmountNum;
      const usdValue = sizeValue * currentPrice;
      setCloseAmount(usdValue.toFixed(2));
      setValueMode("value");
    } else {
      // Switching to Size - convert current USD amount to size
      const usdValue = closeAmountNum;
      const sizeValue = usdValue / currentPrice;
      setCloseAmount(sizeValue.toFixed(4));
      setValueMode("size");
    }
    // Keep selected percentage when toggling mode
  }, [position, closeAmountNum, valueMode, livePrice]);
  
  // Don't render JSX if no position (but all hooks above are still called)
  if (!position || !open) return null;
  
  // Use live price if available, otherwise fall back to position's currentPrice
  const currentPrice = livePrice > 0 ? livePrice : position.currentPrice;
  
  // Get base currency symbol (BTC, ETH, SOL, etc.) - just the first part
  const baseCurrency = position.market.split('-')[0];
  
  // Convert amount to size based on current mode, clamping to position size
  let closeSize: number;
  let closeValue: number;
  if (valueMode === "size") {
    closeSize = Math.min(closeAmountNum, position.size); // Never exceed position size
    closeValue = closeSize * currentPrice;
  } else {
    const calculatedSize = closeAmountNum / currentPrice;
    closeSize = Math.min(calculatedSize, position.size); // Never exceed position size
    closeValue = closeSize * currentPrice;
  }
  
  // Calculate metrics
  const calculatedPercentage = position.size > 0 ? (closeSize / position.size) * 100 : 0;
  
  // Use selected percentage if available (from button click), otherwise use calculated
  // This ensures button always shows exact percentage (50%, not 49%)
  const closePercentage = selectedPercentage ?? Math.min(calculatedPercentage, 100);
  
  const remainingSize = Math.max(0, position.size - closeSize); // Prevent negative due to rounding
  const remainingValue = remainingSize * currentPrice;
  const entryValue = closeSize * position.entryPrice;
  const exitValue = closeSize * currentPrice;
  const realizedPnl = position.side === "long" 
    ? exitValue - entryValue 
    : entryValue - exitValue;
  
  // Calculate ROI% on the closing portion
  const marginForClosePortion = entryValue / position.leverage;
  const roiPercent = marginForClosePortion > 0 
    ? (realizedPnl / marginForClosePortion) * 100 
    : 0;
  
  // Validation
  const maxSize = position.size;
  const maxValue = position.size * currentPrice;
  const isValidAmount = closeAmountNum > 0 && 
    closeSize <= maxSize * 1.0001 && // Allow tiny rounding tolerance
    closeAmount !== "";
  const isValid = isValidAmount && calculatedPercentage <= 100.1; // Prevent >100%
  
  const isLong = position.side === "long";
  const isProfitable = realizedPnl >= 0;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] p-0 gap-0 overflow-hidden bg-card border-border" showCloseButton={false}>
        {/* Header */}
        <DialogHeader className="px-7 pt-7 pb-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg font-semibold">
                Close Position
              </DialogTitle>
              <span className={cn(
                "text-xs font-medium px-2 py-0.5 rounded",
                isLong ? "bg-bid/20 text-bid-foreground" : "bg-ask/20 text-ask-foreground"
              )}>
                {position.market} {isLong ? "Long" : "Short"}
              </span>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </DialogHeader>
        
        <div className="px-7 pt-6 pb-7 space-y-5">
          {/* Input Label */}
          <div>
            <label className="text-sm text-muted-foreground block mb-3">
              Close {valueMode === "size" ? "Size" : "Value"} ({valueMode === "size" ? baseCurrency : "USD"})
            </label>
            
            {/* Input with +/- buttons and currency toggle */}
            <div className="flex items-center bg-muted/50 rounded-lg border border-border overflow-hidden">
              <input
                type="text"
                inputMode="decimal"
                value={closeAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty, or valid decimal numbers
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setCloseAmount(value);
                    setSelectedPercentage(null); // User is manually editing, clear selected percentage
                  }
                }}
                placeholder={`Enter amount in ${valueMode === "size" ? baseCurrency : "USD"}`}
                className="flex-1 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground/60"
              />
              <div className="flex items-center border-l border-border">
                <button
                  onClick={handleDecrement}
                  disabled={closeAmountNum <= 0}
                  className="px-3 py-3 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <div className="w-px h-5 bg-border" />
                <button
                  onClick={handleIncrement}
                  disabled={closeAmountNum >= (valueMode === "size" ? maxSize : maxValue)}
                  className="px-3 py-3 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <div className="w-px h-5 bg-border" />
                <button
                  onClick={handleToggleMode}
                  className="px-3 py-3 text-muted-foreground hover:text-foreground transition-colors font-medium text-xs"
                  title={`Switch to ${valueMode === "size" ? "USD" : baseCurrency}`}
                >
                  <ArrowLeftRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Percentage Buttons */}
          <div className="flex gap-2">
            {PERCENTAGE_OPTIONS.map((pct) => (
              <button
                key={pct}
                onClick={() => handlePercentageClick(pct)}
                className={cn(
                  "flex-1 py-1.5 text-xs font-medium rounded border transition-colors",
                  Math.abs(closePercentage - pct) < 0.5
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                )}
              >
                {pct}%
              </button>
            ))}
          </div>
          
          {/* Info Rows - Simplified display */}
          <div className="space-y-2.5 pt-2">
            {/* Primary metric - switches based on mode */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {valueMode === "size" ? "Close Size" : "Close Value"}
              </span>
              <span className="font-medium">
                {valueMode === "size" 
                  ? `${formatCurrency(closeSize, 4)} ${baseCurrency}`
                  : `$${formatCurrency(closeValue, 2)}`
                }
              </span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {valueMode === "size" ? "Remaining Size" : "Remaining Value"}
              </span>
              <span className="font-medium">
                {valueMode === "size" 
                  ? `${formatCurrency(remainingSize, 4)} ${baseCurrency}`
                  : `$${formatCurrency(remainingValue, 2)}`
                }
              </span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Entry Price</span>
              <span className="font-medium">${formatCurrency(position.entryPrice, 2)}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Current Price</span>
              <span className="font-medium">${formatCurrency(currentPrice, 2)}</span>
            </div>
            
            {closeSize > 0 && (
              <>
                <div className="flex items-center justify-between text-sm pt-1 border-t border-border/40">
                  <span className="text-muted-foreground">Est. Realized P&L</span>
                  <span className={cn(
                    "font-medium",
                    isProfitable ? "text-bid-foreground" : "text-ask-foreground"
                  )}>
                    {isProfitable ? "+" : ""}${formatCurrency(Math.abs(realizedPnl), 2)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">ROI</span>
                  <span className={cn(
                    "font-medium",
                    isProfitable ? "text-bid-foreground" : "text-ask-foreground"
                  )}>
                    {isProfitable ? "+" : ""}{roiPercent.toFixed(2)}%
                  </span>
                </div>
              </>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-11"
              disabled={buttonState !== 'idle'}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className={cn(
                "flex-1 h-11 text-white transition-all duration-150",
                buttonState === 'success'
                  ? "bg-bid hover:bg-bid"
                  : buttonState === 'processing'
                    ? "bg-muted-foreground hover:bg-muted-foreground"
                    : isProfitable 
                      ? "bg-buy hover:bg-buy/90" 
                      : "bg-sell hover:bg-sell/90"
              )}
              disabled={!isValid || buttonState !== 'idle'}
            >
              {buttonState === 'processing' ? (
                <div className="relative">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : buttonState === 'success' ? (
                <Check className="h-5 w-5" />
              ) : (
                `Close ${Math.round(closePercentage)}%`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

